import { Box, Button, List, Paper, Stack, Typography } from "@mui/material";
import { Link } from "react-router";
import type { AssistantTaskRunListItem } from "../../../../api/types/assistantTypes";
import useClientContext from "../../../../context/useClientContext";
import { getChatPath } from "../assistantPaths";
import RunStatusChip from "../RunStatusChip";
import RunPreview from "./RunPreview";
import TaskRunRow from "./TaskRunRow";
import { formatTaskTime } from "./taskStatus";

interface Props {
  runs: AssistantTaskRunListItem[];
  latestRunPreview?: string;
}

const TaskRuns = ({ runs, latestRunPreview }: Props) => {
  const { clientCode } = useClientContext();

  const [latestRun, ...earlierRuns] = runs;
  if (!latestRun) {
    return <Typography color="textSecondary">This task hasn&apos;t run yet.</Typography>;
  }

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle1">Latest run</Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <RunStatusChip status={latestRun.status} height="smaller" />
              <Typography color="textSecondary">{formatTaskTime(latestRun.createdAt)}</Typography>
            </Stack>
          </Box>
          {latestRunPreview && <RunPreview text={latestRunPreview} />}
          <Box>
            <Button component={Link} to={getChatPath(clientCode, latestRun.threadId)}>
              Open chat
            </Button>
          </Box>
        </Stack>
      </Paper>
      {earlierRuns.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="overline" color="textSecondary">
            Earlier runs
          </Typography>
          <Paper variant="outlined">
            <List disablePadding>
              {earlierRuns.map((run, index) => (
                <TaskRunRow
                  key={run.runId}
                  run={run}
                  to={getChatPath(clientCode, run.threadId)}
                  divider={index < earlierRuns.length - 1}
                />
              ))}
            </List>
          </Paper>
        </Stack>
      )}
    </Stack>
  );
};

export default TaskRuns;
